import { Controller } from "@hotwired/stimulus"

// Fire-and-forget product tracking events to the WAD API for WAD-backed
// records, preserving customer-facing stats after the Lab Escape cutover.
//
// Events:
//   view  — posted once when the element is 40% visible (card impressions)
//   click — posted on click (card clicks)
//   page  — posted on connect (direct detail page views)
//
// A tracked card click marks the navigation via sessionStorage so the
// detail page skips its page event, matching legacy counting semantics.
//
// The body is form-encoded to keep requests CORS-simple (no preflight),
// so events count from any origin. No-ops without the wad-api-base-url
// meta tag.
export default class extends Controller {
  static values = {
    view: String,
    click: String,
    page: String,
    payload: Object,
    suppressKey: String
  }

  static clickedKey = "wad-tracking:clicked"

  connect() {
    if (document.documentElement.hasAttribute("data-turbo-preview")) return

    if (this.pageValue) this.#trackPage()
    if (this.viewValue) this.#observeVisibility()
  }

  disconnect() {
    this.observer?.disconnect()
  }

  click(event) {
    if (this.clickValue) this.#post(this.clickValue)
    if (this.suppressKeyValue && this.#navigatesHere(event)) {
      sessionStorage.setItem(this.constructor.clickedKey, this.suppressKeyValue)
    }
  }

  #trackPage() {
    const clicked = sessionStorage.getItem(this.constructor.clickedKey)
    sessionStorage.removeItem(this.constructor.clickedKey)

    if (!(this.suppressKeyValue && clicked === this.suppressKeyValue)) this.#post(this.pageValue)
  }

  #navigatesHere(event) {
    return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
  }

  #observeVisibility() {
    this.observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.4)) {
        this.observer.disconnect()
        this.#post(this.viewValue)
      }
    }, { threshold: 0.4 })

    this.observer.observe(this.element)
  }

  #post(eventType) {
    const baseUrl = document.querySelector('meta[name="wad-api-base-url"]')?.content
    if (!baseUrl) return

    const body = new URLSearchParams({ event_type: eventType })
    Object.entries(this.payloadValue).forEach(([key, value]) => body.append(`event_data[${key}]`, value))

    fetch(`${baseUrl}/v2/tracking/event`, { method: "POST", body, keepalive: true }).catch(() => {})
  }
}
